import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MapPin, ClipboardList, Package, Settings, Eye, Video, Link2, Check, FileText, ChevronDown, ChevronUp, Globe, Lock, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchInput } from "@/components/ui/search-input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { createEvent, updateEvent, uploadEventImage } from "@/lib/api-producer";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { convertImageToWebP } from "@/lib/image";
import { CheckoutQuestionsBuilder } from "@/components/producer/CheckoutQuestionsBuilder";
import { EventProductsManager } from "@/components/producer/EventProductsManager";
import { VenueStep } from "./event-form/VenueStep";
import { InfoStep } from "./event-form/InfoStep";
import { getCategoryLabel } from "@/lib/categories";
import { generateUniqueSlug } from "@/lib/slug";
import { generateStreamingRedirectToken } from "@/lib/streaming";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  convertIsoToLocalDateTime,
  convertLocalDateTimeToIso,
  formatEventDate,
  formatEventTime,
  getBrowserEventTimeZone,
  getEventTimeZoneLabel,
  resolveEventTimeZone,
} from "@/lib/event-timezone";
import { ensureAbsoluteUrl } from "@/lib/urls";
import { scrollPageToTop } from "@/lib/scroll";
import {
  PanelFieldLabel,
  PanelSectionHeading,
  panelAccentDot,
  panelAlert,
  panelBody,
  panelChoice,
  panelChoiceOn,
  panelControl,
  panelEmpty,
  panelHint,
  panelPage,
  panelPill,
  panelSurface,
  panelTitle,
} from "@/components/producer/panel";

const STEPS = [
  { key: "type", icon: Globe },
  { key: "info", icon: FileText },
  { key: "venue", icon: MapPin },
  { key: "form", icon: ClipboardList },
  { key: "products", icon: Package },
  { key: "settings", icon: Settings },
  { key: "review", icon: Eye },
];

export default function ProducerEventForm({ returnPath = "/produtor/eventos" }: { onCancel?: () => void; returnPath?: string }) {
  const { t } = useTranslation("producerEventForm");
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, role, profile } = useAuth();
  const { activeContext, postableOrganizations } = useOrganization();
  const queryClient = useQueryClient();
  const isAdmin = role === "admin";
  const [step, setStep] = useState(isEdit ? 1 : 0);
  const [mobileStepsOpen, setMobileStepsOpen] = useState(false);
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);
  const [targetOrgId, setTargetOrgId] = useState<string>("");
  const [isTransferConfirmOpen, setIsTransferConfirmOpen] = useState(false);
  const [orgSearch, setOrgSearch] = useState("");

  // Admins podem transferir para qualquer organização do site, não só as suas.
  const { data: allOrganizations = [] } = useQuery({
    queryKey: ["admin-all-organizations"],
    enabled: isAdmin,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, slug, logo_url")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data || []).map((o) => ({
        org_id: o.id,
        org_name: o.name,
        org_slug: o.slug,
        org_logo: o.logo_url,
        org_role: "admin",
        member_status: "active",
        joined_at: "",
        custom_permissions: null,
      }));
    },
  });

  // Lista usada nos seletores de organização: admins veem todas; demais, só as suas.
  const orgOptions = isAdmin ? allOrganizations : postableOrganizations;

  const transferMutation = useMutation({
    mutationFn: async (destOrgId: string) => {
      const { data, error } = await supabase.rpc("event_transfer", {
        p_event_id: id,
        p_target_org_id: destOrgId === "personal" ? null : destOrgId,
      });
      if (error) throw error;
      const result = data;
      if (result?.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: (result) => {
      const actualDest = result.to_org || "personal";
      toast({ title: t("form.toasts.transferSuccess") });
      setForm((prev) => ({ ...prev, organization_id: actualDest }));
      setInitialSnapshot((prev) => prev ? ({ ...prev, organization_id: actualDest }) : null);
      queryClient.invalidateQueries({ queryKey: ["producer-events"] });
      queryClient.invalidateQueries({ queryKey: ["producer-event-panel", id] });
      queryClient.invalidateQueries({ queryKey: ["edit-event", id] });
      setTargetOrgId("");
      setIsTransferConfirmOpen(false);
    },
    onError: (err) => {
      toast({
        title: t("form.toasts.transferError"),
        description: err.message || t("form.toasts.transferErrorDesc"),
        variant: "destructive",
      });
    },
  });

  const canManageEvent = role === "admin" || role === "producer";

  const [form, setForm] = useState({
    title: "", slug: "", description: "", category: "other",
    start_date: "", end_date: "", doors_open_time: "",
    time_zone: getBrowserEventTimeZone(),
    venue_name: "", venue_address: "", venue_city: "", venue_state: "", venue_zip: "",
    venue_number: "", venue_complement: "", venue_neighborhood: "",
    is_online: false, online_url: "", online_platform: "external",
    configure_online_url_later: false,
    cover_image_url: "", status: "draft",
    has_certificates: false,
    visibility: "public",
    organization_id: activeContext.organizationId || "personal",
  });

  const [initialSnapshot, setInitialSnapshot] = useState<typeof form | null>(null);
  const isEndDateInvalid = useMemo(() => {
    if (!form.start_date || !form.end_date) return false;
    return new Date(form.start_date) >= new Date(form.end_date);
  }, [form.start_date, form.end_date]);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string>("");
  const [cropImageSrc, setCropImageSrc] = useState("");

  // ── Dirty detection & sticky save bar logic ──
  const hasPendingFiles = !!coverFile;
  const isDirty = useMemo(() => {
    if (!initialSnapshot) return hasPendingFiles;
    return JSON.stringify(form) !== JSON.stringify(initialSnapshot) || hasPendingFiles;
  }, [form, initialSnapshot, hasPendingFiles]);

  const emitDirty = (dirty: boolean) => {
    window.dispatchEvent(new CustomEvent("producer-form-dirty", { detail: { isDirty: dirty } }));
  };

  useEffect(() => {
    emitDirty(isDirty);
  }, [isDirty]);

  // Intercept external navigation (reload, close tab, click on <a> links)
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      const url = new URL(href, window.location.href);
      // Ignore external links, hash-only changes, and same-path navigations
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      e.preventDefault();
      setPendingNavigation(() => () => navigate(href));
      setShowUnsavedDialog(true);
    };
    document.addEventListener("click", handleLinkClick, true);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [isDirty]);

  const handlePersist = async () => {
    await handleSave(false);
  };

  const handleDiscardChanges = () => {
    if (!initialSnapshot) return;
    setForm(initialSnapshot);
    setCoverFile(null);
    setCoverPreview(initialSnapshot.cover_image_url || "");
    setCropImageSrc("");
  };

  const confirmSaveAndNavigate = async () => {
    setShowUnsavedDialog(false);
    try {
      await handleSave(false);
      pendingNavigation?.();
    } catch {
      // permanece na página se falhar
    } finally {
      setPendingNavigation(null);
    }
  };

  const confirmDiscardAndNavigate = () => {
    handleDiscardChanges();
    setShowUnsavedDialog(false);
    pendingNavigation?.();
    setPendingNavigation(null);
  };

  // Listen for parent save/discard requests
  useEffect(() => {
    const onSaveRequest = () => {
      if (!isDirty) {
        window.dispatchEvent(new CustomEvent("producer-form-persisted"));
        return;
      }
      handlePersist().then(() => {
        window.dispatchEvent(new CustomEvent("producer-form-persisted"));
      }).catch(() => {
        // erro já é tratado no handleSave
      });
    };
    const onDiscardRequest = () => {
      handleDiscardChanges();
      window.dispatchEvent(new CustomEvent("producer-form-discarded"));
    };
    window.addEventListener("producer-form-persist-request", onSaveRequest);
    window.addEventListener("producer-form-discard-request", onDiscardRequest);
    return () => {
      window.removeEventListener("producer-form-persist-request", onSaveRequest);
      window.removeEventListener("producer-form-discard-request", onDiscardRequest);
    };
  }, [isDirty, form, initialSnapshot, coverFile]);

  // ── Queries ──
  const { data: editEventData } = useQuery({
    queryKey: ["edit-event", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("events").select("*, ticket_tiers(*)").eq("id", id).single();
      if (error) throw error;
      return data;
    },
    enabled: isEdit,
  });


  useEffect(() => {
    if (!isEdit && !initialSnapshot) {
      setInitialSnapshot({ ...form });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const eventTiers = (editEventData)?.ticket_tiers;
    if (!eventTiers) return;

    const eventTimeZone = resolveEventTimeZone((editEventData).time_zone);

    // Parse concatenated address back into components
    // Format: "Street, Number - Complement, Neighborhood"
    let parsedAddress = editEventData.venue_address || "";
    let parsedNumber = "";
    let parsedComplement = "";
    let parsedNeighborhood = "";
    if (parsedAddress) {
      const dashParts = parsedAddress.split(" - ");
      const beforeDash = dashParts[0];
      const afterDash = dashParts.slice(1).join(" - ");
      const commaParts = beforeDash.split(", ");
      if (commaParts.length >= 2) {
        parsedAddress = commaParts[0];
        parsedNumber = commaParts[1];
      }
      if (afterDash) {
        const compParts = afterDash.split(", ");
        parsedComplement = compParts[0] || "";
        parsedNeighborhood = compParts.slice(1).join(", ") || "";
      } else if (commaParts.length >= 3) {
        parsedNeighborhood = commaParts.slice(2).join(", ");
      }
    }

    const nextForm = {
      title: editEventData.title || "", slug: editEventData.slug || "", description: editEventData.description || "",
      category: editEventData.category || "other",
      start_date: convertIsoToLocalDateTime(editEventData.start_date, eventTimeZone),
      end_date: convertIsoToLocalDateTime(editEventData.end_date, eventTimeZone),
      doors_open_time: convertIsoToLocalDateTime(editEventData.doors_open_time, eventTimeZone),
      time_zone: eventTimeZone,
      venue_name: editEventData.venue_name || "", venue_address: parsedAddress,
      venue_city: editEventData.venue_city || "", venue_state: editEventData.venue_state || "",
      venue_zip: editEventData.venue_zip || "", venue_number: parsedNumber, venue_complement: parsedComplement, venue_neighborhood: parsedNeighborhood,
      is_online: editEventData.is_online || false, online_url: editEventData.online_url || "", online_platform: (editEventData).online_platform || "external",
      configure_online_url_later: !!editEventData.is_online && !editEventData.online_url,
      cover_image_url: editEventData.cover_image_url || "", status: editEventData.status || "draft",
      has_certificates: editEventData.has_certificates || false,
      visibility: (editEventData).visibility || "public",
      organization_id: (editEventData).organization_id || "personal",
    };

    setForm(nextForm);
    setInitialSnapshot(nextForm);
    setCoverPreview(editEventData.cover_image_url || "");
  }, [editEventData]);

  useEffect(() => {
    // Reset image states when id changes or when editEventData changes (in case of reloads)
    if (editEventData?.cover_image_url) {
      setCoverPreview(editEventData.cover_image_url);
      setCoverFile(null);
      setCropImageSrc("");
    }
  }, [id, editEventData?.cover_image_url]);

  const generateSlug = (title: string) => title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const updateField = (field: string, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "title") {
        // Auto-generate slug if it's currently empty, even in edit mode.
        // This ensures events created without a slug get one suggested when title is touched.
        if (!form.slug || !isEdit) {
          updated.slug = generateSlug(value);
        }
      }
      return updated;
    });
  };

  const selectedTimeZone = resolveEventTimeZone(form.time_zone);
  const selectedTimeZoneLabel = getEventTimeZoneLabel(selectedTimeZone);


  const objectUrlsRef = useRef<string[]>([]);

  const revokeObjectUrls = () => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current = [];
  };

  const createObjectUrl = (file: File) => {
    const url = URL.createObjectURL(file);
    objectUrlsRef.current.push(url);
    return url;
  };

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCropImageSrc(createObjectUrl(file));
    }
    e.target.value = "";
  };

  const handleCropDone = (croppedFile: File) => {
    setCoverFile(croppedFile);
    setCoverPreview(createObjectUrl(croppedFile));
    setCropImageSrc("");
  };

  const handleCropCancel = () => {
    setCropImageSrc("");
  };

  useEffect(() => {
    return () => {
      revokeObjectUrls();
    };
  }, []);

  const handleUploadCover = async () => {
    if (!coverFile || !user) return form.cover_image_url;
    
    let processedFile = coverFile;
    try {
      processedFile = await convertImageToWebP(coverFile, { maxWidth: 1200, quality: 0.8 });
    } catch (e) {
      console.error("Erro ao converter capa para WebP, enviando original:", e);
    }

    if (processedFile.size > 5 * 1024 * 1024) {
      throw new Error(t("form.errors.imageTooLarge"));
    }
    const ext = "webp";
    const path = `${user.id}/event-cover-${Date.now()}.${ext}`;
    return await uploadEventImage(path, processedFile);
  };

  const handleSave = async (publish = false) => {
    if (!user || !canManageEvent) {
      toast({ title: t("form.toasts.error"), description: t("form.toasts.unauthenticatedDesc"), variant: "destructive" });
      return;
    }
    try {
      let coverUrl = form.cover_image_url;
      if (coverFile) coverUrl = await handleUploadCover();

      // Ensure unique slug for new events
      let finalSlug = form.slug;
      if (!finalSlug || !isEdit) {
        finalSlug = await generateUniqueSlug(form.title);
      }

      let fullAddress = form.venue_address || "";
      if (form.venue_number) fullAddress += `, ${form.venue_number}`;
      if (form.venue_complement) fullAddress += ` - ${form.venue_complement}`;
      if (form.venue_neighborhood) fullAddress += `, ${form.venue_neighborhood}`;

      const normalizedStartDate = convertLocalDateTimeToIso(form.start_date, selectedTimeZone);
      const normalizedEndDate = convertLocalDateTimeToIso(form.end_date, selectedTimeZone);
      const normalizedDoorsOpenTime = convertLocalDateTimeToIso(form.doors_open_time, selectedTimeZone);

      if (!normalizedStartDate || !normalizedEndDate) {
        throw new Error(t("form.errors.invalidDates"));
      }

      if (new Date(normalizedStartDate) >= new Date(normalizedEndDate)) {
        throw new Error(t("form.errors.endDateBeforeStartDate"));
      }

      if (!form.is_online && normalizedDoorsOpenTime && new Date(normalizedDoorsOpenTime) >= new Date(normalizedStartDate)) {
        throw new Error(t("form.errors.doorsOpenBeforeStart"));
      }

      if (!form.is_online && (!form.venue_state || !form.venue_city)) {
        throw new Error(t("form.errors.missingStateCity"));
      }

      if (form.is_online) {
        if (publish && (form.configure_online_url_later || !form.online_url?.trim())) {
          throw new Error(t("form.errors.missingOnlineUrlToPublish"));
        }
        if (!publish && !form.configure_online_url_later && !form.online_url?.trim()) {
          throw new Error(t("form.errors.missingOnlineUrl"));
        }
      }

      const eventData = {
        title: form.title, slug: finalSlug, description: form.description,
        category: form.category, venue_name: form.venue_name,
        venue_address: fullAddress, venue_city: form.venue_city,
        venue_state: form.venue_state, venue_zip: form.venue_zip,
        is_online: form.is_online, online_url: ensureAbsoluteUrl(form.online_url),
        online_platform: form.online_platform, visibility: form.visibility,
        cover_image_url: coverUrl, has_certificates: form.has_certificates,
        time_zone: selectedTimeZone,
        start_date: normalizedStartDate,
        end_date: normalizedEndDate,
        doors_open_time: normalizedDoorsOpenTime,
        status: publish ? "published" : form.status,
        organization_id: form.organization_id === "personal" ? null : form.organization_id,
      };

      if (isEdit) {
        await updateEvent(id!, eventData);
      } else {
        const createData = { ...eventData, producer_id: user.id, created_by: user.id, streaming_redirect_token: null as string | null, streaming_url: null as string | null };
        // Generate streaming redirect token for online events
        if (form.is_online) {
          createData.streaming_redirect_token = await generateStreamingRedirectToken(finalSlug);
          createData.streaming_url = ensureAbsoluteUrl(form.online_url) || null;
        }
        await createEvent(createData);
      }



      queryClient.invalidateQueries({ queryKey: ["producer-events"] });
      if (id) {
        queryClient.invalidateQueries({ queryKey: ["producer-event-panel", id] });
        queryClient.invalidateQueries({ queryKey: ["event-dash-meta", id] });
        queryClient.invalidateQueries({ queryKey: ["edit-event", id] });
      }
      toast({ title: publish ? t("form.toasts.publishSuccess") : t("form.toasts.saveSuccess") });

      // Update dirty-state snapshot and clear pending files
      setInitialSnapshot({ ...form });
      setCoverFile(null);

      // Se tiver uma nova foto, invalidamos os dados para garantir que tudo seja recarregado
      if (coverFile) {
        queryClient.invalidateQueries({ queryKey: ["edit-event", id] });
        queryClient.invalidateQueries({ queryKey: ["producer-event-panel", id] });
      }

      // When editing, we don't need a hard reload as React Query invalidation
      // will trigger an update of all relevant components.
      if (!isEdit) {
        navigate(returnPath);
      }
    } catch (err) {
      const message = err?.message || JSON.stringify(err);
      toast({ title: t("form.toasts.saveError"), description: message, variant: "destructive" });
      console.error("ProducerEventForm save error:", err);
    }
  };

  const visibleSteps = STEPS.filter((step) => {
    if (isEdit && step.key === "type") return false;
    if (!isEdit && (step.key === "form" || step.key === "products")) return false;
    return true;
  });
  const isInlineMode = !!searchParams.get("editorStep");
  const currentStepData = STEPS[step];
  const currentVisibleStepIndex = visibleSteps.findIndex((s) => STEPS.indexOf(s) === step);
  const currentStepNumber = currentVisibleStepIndex >= 0 ? currentVisibleStepIndex + 1 : 1;
  const completionPercent = Math.min(100, Math.max(0, Math.round((currentStepNumber / visibleSteps.length) * 100)));

  const handleNextStep = () => {
    const nextIdx = currentVisibleStepIndex + 1;
    if (nextIdx < visibleSteps.length) {
      setStep(STEPS.indexOf(visibleSteps[nextIdx]));
    }
  };

  const handlePrevStep = () => {
    const prevIdx = currentVisibleStepIndex - 1;
    if (prevIdx >= 0) {
      setStep(STEPS.indexOf(visibleSteps[prevIdx]));
    }
  };

  useEffect(() => {
    if (!isEdit) return;
    const requestedStep = searchParams.get("step") || searchParams.get("editorStep");
    if (!requestedStep) return;

    const stepIndex = STEPS.findIndex((s) => s.key === requestedStep);
    if (stepIndex >= 1) {
      setStep(stepIndex);
    }
  }, [isEdit, searchParams]);

  useLayoutEffect(() => {
    scrollPageToTop();
  }, [step]);

  useEffect(() => {
    const onSaveRequest = (event: Event) => {
      const customEvent = event as CustomEvent<{ publish?: boolean }>;
      const shouldPublish = !!customEvent.detail?.publish;
      void handleSave(shouldPublish);
    };

    window.addEventListener("producer-event-editor-save", onSaveRequest);
    return () => window.removeEventListener("producer-event-editor-save", onSaveRequest);
  }, []);

  return (
    <div className={cn(
      "flex flex-col gap-6",
      isInlineMode
        ? "min-h-0 pb-32 max-[768px]:pb-44"
        : "lg:flex-row min-h-[calc(100vh-8rem)] lg:h-full lg:min-h-0 lg:overflow-hidden"
    )} data-event-editor-form>
      {/* Sidebar Steps - Desktop */}
      {!isInlineMode && (
      <nav className="hidden lg:flex flex-col w-72 shrink-0 h-full border-r border-border/60 pr-4">
        <div className="h-full flex flex-col">
          <div className="mb-4 rounded-xl border border-border/70 bg-muted/20 p-3">
            <h2 className="font-display text-base font-bold text-foreground">
              {isEdit ? t("form.editTitle") : t("form.createTitle")}
            </h2>
            {activeContext.type === "organization" && activeContext.organizationName && !isEdit && (
              <p className="mt-0.5 text-[11px] text-primary font-medium">
                {t("form.onBehalfOf", { name: activeContext.organizationName })}
              </p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              {t("form.stepIndicator", { current: currentStepNumber, total: visibleSteps.length, label: t(`form.steps.${currentStepData?.key}.label`) })}
            </p>
            <Progress value={completionPercent} className="mt-2 h-2" />
          </div>

          {visibleSteps.map((s) => {
            const realIndex = STEPS.indexOf(s);
            const Icon = s.icon;
            const isActive = realIndex === step;
            const isDone = realIndex < step;
            return (
              <button
                key={s.key}
                onClick={() => setStep(realIndex)}
                className={cn(
                  "mb-1 flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-all",
                  isActive && "border-primary/30 bg-primary/10 text-primary",
                  isDone && !isActive && "border-transparent text-primary hover:bg-primary/5",
                  !isActive && !isDone && "border-transparent text-muted-foreground hover:bg-muted"
                )}
              >
                <span className={cn(
                  "flex items-center justify-center w-6 h-6 rounded-full text-xs shrink-0",
                  isActive && "bg-primary/20",
                  isDone && !isActive && "bg-primary/15",
                  !isActive && !isDone && "bg-muted"
                )}>
                  {isDone ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{t(`form.steps.${s.key}.label`)}</span>
                  <span className={cn("block truncate text-xs", isActive ? "text-primary/80" : "text-muted-foreground")}>
                    {t(`form.steps.${s.key}.hint`)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </nav>
      )}

      {/* Mobile Step Indicator */}
      {!isInlineMode && (
      <div className="lg:hidden">
        <button
          onClick={() => setMobileStepsOpen(!mobileStepsOpen)}
          className="flex items-center justify-between w-full rounded-xl border border-border bg-card shadow-sm px-4 py-3.5 text-sm font-semibold transition-all hover:bg-muted/30"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">
              {currentStepNumber}
            </span>
            <span className="truncate text-foreground">
              {t("form.stepIndicator", { current: currentStepNumber, total: visibleSteps.length, label: "" })} <span className="font-bold text-primary">{t(`form.steps.${currentStepData?.key}.label`)}</span>
            </span>
          </div>
          {mobileStepsOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground transition-transform shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform shrink-0" />
          )}
        </button>
        <Progress value={completionPercent} className="mt-2 h-2" />
        {mobileStepsOpen && (
          <div className="mt-2.5 rounded-2xl border border-border/80 bg-background/95 backdrop-blur-md p-2.5 space-y-1 shadow-xl animate-in slide-in-from-top-2 duration-200">
            {visibleSteps.map((s) => {
              const realIndex = STEPS.indexOf(s);
              const isActive = realIndex === step;
              const isDone = realIndex < step;
              const Icon = s.icon;
              return (
                <button
                  key={s.key}
                  onClick={() => { setStep(realIndex); setMobileStepsOpen(false); }}
                  className={cn(
                    "flex w-full items-center gap-3.5 rounded-xl px-3.5 py-2.5 text-sm transition-all border",
                    isActive
                      ? "bg-primary/5 border-primary/20 text-primary font-semibold"
                      : isDone
                      ? "border-transparent text-primary/80 hover:bg-primary/5"
                      : "border-transparent text-muted-foreground hover:bg-muted/50"
                  )}
                >
                  <span className={cn(
                    "flex items-center justify-center w-7 h-7 rounded-full text-xs shrink-0 transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground font-bold"
                      : isDone
                      ? "bg-primary/10 text-primary"
                      : "bg-muted text-muted-foreground"
                  )}>
                    {isDone ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                  </span>
                  <span className="min-w-0 text-left flex-1">
                    <span className={cn("block truncate text-sm font-semibold", isActive ? "text-primary" : "text-foreground")}>{t(`form.steps.${s.key}.label`)}</span>
                    <span className="block truncate text-[11px] text-muted-foreground font-normal mt-0.5">
                      {t(`form.steps.${s.key}.hint`)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Main Content */}
      <div className={cn(
        "flex-1 space-y-6 pb-8",
        isInlineMode ? "max-w-none w-full" : "max-w-3xl lg:h-full lg:overflow-y-auto lg:pr-2"
      )} data-scroll-container>
        {/* Step 0: Event Type Selection */}
        {currentStepData?.key === "type" && !isEdit && (
          <div className="space-y-6">
            <Card className="border-border/60 shadow-none overflow-hidden">
              <CardHeader className="pb-4 border-b border-border/40">
                <CardTitle className="text-lg font-bold tracking-tight">{t("form.sections.type.title")}</CardTitle>
                <CardDescription>{t("form.sections.type.description")}</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <RadioGroup
                  value={form.is_online ? "online" : "presencial"}
                  onValueChange={(v) => updateField("is_online", v === "online")}
                  className="grid grid-cols-2 gap-3 sm:gap-4"
                >
                  <label className={cn(
                    "flex flex-col items-center text-center p-3.5 rounded-xl border-2 cursor-pointer transition-all",
                    "sm:flex-row sm:items-start sm:text-left sm:gap-4 sm:p-4",
                    !form.is_online ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                  )}>
                    <RadioGroupItem value="presencial" className="mt-1 shrink-0 hidden sm:block" />
                    <div className="flex-1 flex flex-col items-center sm:items-start">
                      <div className="flex flex-col sm:flex-row items-center gap-2 mb-1">
                        <MapPin className="h-6 w-6 sm:h-5 sm:w-5 text-primary shrink-0" />
                        <span className="font-semibold text-sm sm:text-base text-foreground">{t("form.sections.type.inPerson.title")}</span>
                      </div>
                      <p className="text-[11px] sm:text-sm text-muted-foreground leading-normal">
                        {t("form.sections.type.inPerson.description")}
                      </p>
                    </div>
                  </label>

                  <label className={cn(
                    "flex flex-col items-center text-center p-3.5 rounded-xl border-2 cursor-pointer transition-all",
                    "sm:flex-row sm:items-start sm:text-left sm:gap-4 sm:p-4",
                    form.is_online ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                  )}>
                    <RadioGroupItem value="online" className="mt-1 shrink-0 hidden sm:block" />
                    <div className="flex-1 flex flex-col items-center sm:items-start">
                      <div className="flex flex-col sm:flex-row items-center gap-2 mb-1">
                        <Globe className="h-6 w-6 sm:h-5 sm:w-5 text-primary shrink-0" />
                        <span className="font-semibold text-sm sm:text-base text-foreground">{t("form.sections.type.online.title")}</span>
                      </div>
                      <p className="text-[11px] sm:text-sm text-muted-foreground leading-normal">
                        {t("form.sections.type.online.description")}
                      </p>
                    </div>
                  </label>
                </RadioGroup>
              </CardContent>
            </Card>

          </div>
        )}

        {currentStepData?.key === "info" && (
          <InfoStep
            form={form}
            updateField={updateField}
            cropImageSrc={cropImageSrc}
            coverPreview={coverPreview}
            onCropDone={handleCropDone}
            onCropCancel={handleCropCancel}
            onCoverSelect={handleCoverSelect}
            eventId={(editEventData)?.id}
            isEndDateInvalid={isEndDateInvalid}
            selectedTimeZoneLabel={selectedTimeZoneLabel}
            organizerSlug={profile?.organizer_slug}
          />
        )}
        {/* Step 2: Venue */}
        {currentStepData?.key === "venue" && <VenueStep form={form} updateField={updateField} />}

        {/* Step: Checkout Questions */}
        {currentStepData?.key === "form" && (
          isEdit ? <CheckoutQuestionsBuilder eventId={id!} /> : (
            <div className={panelPage}>
              <header>
                <h1 className={panelTitle}>{t("form.steps.form.label")}</h1>
              </header>
              <div className={cn(panelEmpty, "flex items-center justify-center px-6 py-16 text-center")}>
                <p className="text-sm text-muted-foreground">{t("form.sections.checkoutQuestions.hint")}</p>
              </div>
            </div>
          )
        )}

        {/* Step: Products */}
        {currentStepData?.key === "products" && (
          isEdit ? <EventProductsManager eventId={id!} /> : (
            <div className={panelPage}>
              <header>
                <h1 className={panelTitle}>{t("form.steps.products.label")}</h1>
              </header>
              <div className={cn(panelEmpty, "flex flex-col items-center justify-center px-6 py-16 text-center")}>
                <p className="text-sm font-medium text-foreground">{t("form.sections.products.availableAfterSave")}</p>
                <p className={panelHint}>{t("form.sections.products.hint")}</p>
              </div>
            </div>
          )
        )}

        {/* Step 3: Settings */}
        {currentStepData?.key === "settings" && (
          <div className={panelPage}>
            <header>
              <h1 className={panelTitle}>{t("form.steps.settings.label")}</h1>
            </header>

            <div className={panelSurface}>
              <PanelSectionHeading>{t("form.sections.settings.title")}</PanelSectionHeading>
              <div className={panelBody}>
                <div>
                  <PanelFieldLabel>{t("form.sections.settings.privacy")}</PanelFieldLabel>
                  <RadioGroup
                    value={form.visibility}
                    onValueChange={(v) => updateField("visibility", v)}
                    className="mt-2 grid grid-cols-2 gap-3"
                  >
                    <label className={cn(panelChoice, "flex-row items-start gap-3", form.visibility === "public" && panelChoiceOn)}>
                      <span className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl",
                        form.visibility === "public" ? "bg-background" : "bg-background/70",
                      )}>
                        <Globe className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-2">
                          {form.visibility === "public" && <span className={panelAccentDot} aria-hidden />}
                          <RadioGroupItem value="public" className="sr-only" />
                          <span className="text-sm font-medium">{t("form.sections.settings.public")}</span>
                        </span>
                        <p className={panelHint}>{t("form.sections.settings.publicHint")}</p>
                      </span>
                    </label>
                    <label className={cn(panelChoice, "flex-row items-start gap-3", form.visibility === "private" && panelChoiceOn)}>
                      <span className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl",
                        form.visibility === "private" ? "bg-background" : "bg-background/70",
                      )}>
                        <Lock className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-2">
                          {form.visibility === "private" && <span className={panelAccentDot} aria-hidden />}
                          <RadioGroupItem value="private" className="sr-only" />
                          <span className="text-sm font-medium">{t("form.sections.settings.private")}</span>
                        </span>
                        <p className={panelHint}>{t("form.sections.settings.privateHint")}</p>
                      </span>
                    </label>
                  </RadioGroup>
                </div>

                <div>
                  <PanelFieldLabel>{t("form.sections.settings.organizer")}</PanelFieldLabel>
                  {isEdit ? (
                    <div className="mt-2 max-w-md rounded-2xl bg-secondary/50 px-4 py-3.5">
                      <p className="text-sm font-medium">
                        {form.organization_id === "personal"
                          ? t("form.sections.settings.personalProfile", { name: profile?.full_name || t("common:you") })
                          : orgOptions.find(o => o.org_id === form.organization_id)?.org_name || t("form.sections.settings.assignedOrganization")}
                      </p>
                      <p className={panelHint}>
                        {t("form.sections.settings.organizationWarning")}
                      </p>
                    </div>
                  ) : (
                    <>
                      <Select
                        value={form.organization_id || "personal"}
                        onValueChange={(v) => updateField("organization_id", v)}
                      >
                        <SelectTrigger className={cn("max-w-md", panelControl)}>
                          <SelectValue placeholder={t("form.sections.settings.selectOrganizer")} />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl">
                          <SelectItem value="personal">{t("form.sections.settings.personalProfile", { name: profile?.full_name || t("common:you") })}</SelectItem>
                          {orgOptions.map(org => (
                            <SelectItem key={org.org_id} value={org.org_id}>
                              {org.org_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className={cn(panelHint, "max-w-md")}>
                        {t("form.sections.settings.organizationHint")}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {isEdit && (
              <div className={panelSurface}>
                <PanelSectionHeading>{t("form.sections.transfer.title")}</PanelSectionHeading>
                <div className={panelBody}>
                  <div>
                    <PanelFieldLabel>{t("form.sections.transfer.destinationOrg")}</PanelFieldLabel>
                    {isAdmin && (
                      <SearchInput
                        value={orgSearch}
                        onChange={(e) => setOrgSearch(e.target.value)}
                        placeholder={t("form.sections.transfer.searchPlaceholder")}
                        className={cn("max-w-xl", panelControl)}
                      />
                    )}
                    <div className="mt-3 flex max-w-xl flex-col gap-3 sm:flex-row">
                      <Select value={targetOrgId} onValueChange={setTargetOrgId}>
                        <SelectTrigger className={cn("w-full", panelControl)}>
                          <SelectValue placeholder={t("form.sections.transfer.selectDestination")} />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl">
                          <SelectItem value="personal">{t("form.sections.settings.personalProfile", { name: profile?.full_name || t("common:you") })}</SelectItem>
                          {orgOptions
                            .filter(org => org.org_id !== form.organization_id)
                            .filter(org =>
                              !orgSearch.trim() ||
                              org.org_name.toLowerCase().includes(orgSearch.trim().toLowerCase())
                            )
                            .map(org => (
                              <SelectItem key={org.org_id} value={org.org_id}>
                                {org.org_name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!targetOrgId || targetOrgId === (form.organization_id || "personal") || transferMutation.isPending}
                        onClick={() => setIsTransferConfirmOpen(true)}
                        className={cn("h-12 shrink-0 px-6", panelPill)}
                      >
                        {transferMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {t("form.sections.transfer.transferBtn")}
                      </Button>
                    </div>
                    <p className={panelHint}>
                      {isAdmin
                        ? t("form.sections.transfer.adminHint")
                        : t("form.sections.transfer.userHint")}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 6: Review */}
        {currentStepData?.key === "review" && (
          <Card className="border-border/50 shadow-sm overflow-hidden">
            <CardHeader className="pb-4 border-b border-border/50">
              <CardTitle className="text-xl font-bold tracking-tight">{t("form.sections.review.title")}</CardTitle>
              <CardDescription>{t("form.sections.review.description")}</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {coverPreview && <img src={coverPreview} alt="Capa" className="w-full aspect-[16/9] object-cover rounded-lg" />}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <span className="text-muted-foreground">{t("form.sections.review.eventTitle")}</span>
                <span className="text-foreground font-medium">{form.title}</span>
                <span className="text-muted-foreground">{t("form.sections.review.type")}</span>
                <span className="text-foreground">{form.is_online ? t("form.sections.review.online") : t("form.sections.review.inPerson")}</span>
                <span className="text-muted-foreground">{t("form.sections.review.category")}</span>
                <span className="text-foreground">{getCategoryLabel(form.category)}</span>
                <span className="text-muted-foreground">{t("form.sections.review.start")}</span>
                <span className="text-foreground">
                  {form.start_date ? `${formatEventDate(form.start_date, selectedTimeZone)} • ${formatEventTime(form.start_date, selectedTimeZone)}` : "—"}
                </span>
                <span className="text-muted-foreground">{t("form.sections.review.end")}</span>
                <span className={cn("text-foreground", isEndDateInvalid && "text-destructive font-bold")}>
                  {form.end_date ? `${formatEventDate(form.end_date, selectedTimeZone)} • ${formatEventTime(form.end_date, selectedTimeZone)}` : "—"}
                  {isEndDateInvalid && t("form.sections.review.invalidDate")}
                </span>
                <span className="text-muted-foreground">{t("form.sections.review.timezone")}</span>
                <span className="text-foreground">{selectedTimeZoneLabel}</span>
                <span className="text-muted-foreground">{t("form.sections.review.venue")}</span>
                <span className="text-foreground">{form.is_online ? (form.online_url || t("form.sections.review.urlNotProvided")) : (form.venue_name || "—")}</span>
              </div>

              <Separator />
              <p className="text-xs text-muted-foreground mb-4">
                {t("form.sections.review.termsNotice")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-6 justify-end border-t border-border/50">
                {form.status === "draft" ? (
                  <>
                    <Button 
                      type="button"
                      variant="outline" 
                      onClick={() => handleSave(false)}
                      className="rounded-xl font-medium"
                    >
                      {t("form.sections.review.saveDraft")}
                    </Button>
                    <Button 
                      type="button"
                      onClick={() => setIsPublishConfirmOpen(true)}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl"
                    >
                      {t("form.sections.review.publish")}
                    </Button>
                  </>
                ) : (
                  <Button 
                    type="button"
                    onClick={() => handleSave(false)}
                    className="rounded-xl font-semibold"
                  >
                    {t("form.sections.review.saveChanges")}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation Footer for steps (excluding review step which has its own actions) */}
        {currentStepData?.key !== "review" && (
          <div className="flex justify-between items-center pt-4 border-t border-border/50">
            <Button
              type="button"
              variant="outline"
              onClick={handlePrevStep}
              disabled={currentVisibleStepIndex === 0}
              className="rounded-xl px-5"
            >
              {t("form.actions.back")}
            </Button>
            <Button
              type="button"
              onClick={handleNextStep}
              className="rounded-xl px-5 font-semibold"
            >
              {t("form.actions.next")}
            </Button>
          </div>
        )}

      </div>

      {/* Sticky Save Bar */}
      {isEdit && isDirty && (
        <>
          {/* Desktop: floating card */}
          <div className="hidden max-[768px]:block" />
          <div className="hidden sm:flex fixed bottom-6 right-6 z-50 items-center gap-4 rounded-2xl bg-background border shadow-xl px-6 py-4 animate-in slide-in-from-bottom-5 fade-in duration-300">
            <AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />
            <div className="flex flex-col">
              <span className="text-sm font-medium">{t("form.unsaved.title")}</span>
              <span className="text-xs text-muted-foreground">{t("form.unsaved.description")}</span>
            </div>
            <div className="flex items-center gap-2 ml-2">
              <Button variant="ghost" size="sm" onClick={handleDiscardChanges}>
                {t("form.unsaved.discard")}
              </Button>
              <Button size="sm" onClick={handlePersist} className="font-bold">
                {t("form.unsaved.save")}
              </Button>
            </div>
          </div>

          {/* Mobile: full-width sticky bottom (above action bar) */}
          <div className="sm:hidden fixed inset-x-0 bottom-[5rem] z-50 bg-background/95 backdrop-blur-xl border shadow-lg rounded-t-xl px-4 py-3 animate-in slide-in-from-bottom-full fade-in duration-300">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />
                <span className="text-sm font-medium truncate">{t("form.unsaved.title")}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={handleDiscardChanges}>
                  {t("form.unsaved.discard")}
                </Button>
                <Button size="sm" onClick={handlePersist} className="font-bold">
                  {t("form.unsaved.save")}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Unsaved changes confirmation dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent className="rounded-2xl max-w-[90vw] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("form.unsaved.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("form.unsaved.dialogDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={confirmDiscardAndNavigate} className="bg-secondary text-secondary-foreground hover:bg-secondary/80 border-0 rounded-xl mt-0">
              {t("form.unsaved.discard")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmSaveAndNavigate} className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl">
              {t("form.unsaved.save")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isTransferConfirmOpen} onOpenChange={setIsTransferConfirmOpen}>
        <AlertDialogContent className={cn(panelAlert, "max-w-[90vw]")}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg font-semibold tracking-tight">{t("form.transferConfirm.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("form.transferConfirm.description1")}
              <br />
              <br />
              {t("form.transferConfirm.description2")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            <AlertDialogCancel className={panelPill}>{t("form.actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => transferMutation.mutate(targetOrgId)}
              className={cn(panelPill, "bg-destructive text-destructive-foreground hover:bg-destructive/90")}
            >
              {transferMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("form.sections.transfer.transferBtn")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isPublishConfirmOpen} onOpenChange={setIsPublishConfirmOpen}>
        <AlertDialogContent className="rounded-2xl max-w-[90vw] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("form.publishConfirm.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("form.publishConfirm.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="rounded-xl">{t("form.actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setIsPublishConfirmOpen(false); handleSave(true); }} className="rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
              {t("form.sections.review.publish")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
